import { useQuery } from "@tanstack/react-query";
import type React from "react";

import type { CompanyAdoptionRateDto } from "@/api/types";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { MeterChart } from "@/components/Charts/MeterChart";
import { dataColors } from "@/components/Charts/theme";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";

const FALLBACK_ADOPTION_STATS: CompanyAdoptionRateDto = {
  adoptionRatePercentage: 0,
  reachRatePercentage: 0,
  totalCompanies: 0,
  totalCompaniesWithOnboardedUsers: 0,
  totalCompaniesWithUsers: 0,
  totalResidents: 0,
};

export function CompanyAdoptionWidget(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();

  const analyticsQueries = useAnalyticsQueries();

  const {
    data: adoption,
    isPending: isPendingAdoption,
    isError: isErrorAdoption,
    error: errorAdoption,
    refetch: refetchAdoption,
  } = useQuery(analyticsQueries.companyAdoption());

  const handleReload = () => {
    void refetchAdoption();
  };

  const isLoading = isPendingAdoption;
  const isError = isErrorAdoption;
  const error = errorAdoption;

  const primaryData = adoption || FALLBACK_ADOPTION_STATS;
  const missedCompanies = primaryData.totalCompanies - primaryData.totalCompaniesWithUsers;
  const isDataAvailable = primaryData.totalCompaniesWithUsers > 0 || primaryData.totalCompaniesWithOnboardedUsers > 0;
  const isActionRequired = !isLoading && !isError && isDataAvailable && missedCompanies > 0;

  return (
    <AnalyticsWidgetCard.Root
      title={t("component.analytics-widget.reach-adoption.title")}
      href={isDataAvailable ? routes.analytics.adoptionDetails({ slug }) : undefined}
      isLoading={isLoading}
      isError={isError}
      isDataAvailable={isDataAvailable}
      error={error}
      onReload={handleReload}
    >
      <AnalyticsWidgetCard.Body>
        <MeterChart
          primaryValue={primaryData.reachRatePercentage}
          secondaryValue={primaryData.adoptionRatePercentage}
          ariaLabel="Reach & adoption rate chart"
        />
        <ChartLegend
          vertical
          items={[
            {
              label: (
                <Trans
                  i18nKey="component.analytics-widget.reach-adoption.company.chart.legend.primary"
                  components={{
                    b: <strong />,
                  }}
                  values={{
                    reachCount: primaryData.totalCompaniesWithUsers,
                    count: primaryData.totalCompanies,
                  }}
                />
              ),
              color: dataColors.success,
            },
            {
              label: (
                <Trans
                  i18nKey="component.analytics-widget.reach-adoption.company.chart.legend.secondary"
                  components={{
                    b: <strong />,
                  }}
                  values={{
                    count: primaryData.totalCompaniesWithOnboardedUsers,
                  }}
                />
              ),
              color: dataColors.primary,
            },
          ]}
        />
      </AnalyticsWidgetCard.Body>
      {isActionRequired && (
        <AnalyticsWidgetCard.Footer>
          <Stack gap="lg" align="center">
            <Button type="link" href={routes.companies.list({ slug })}>
              {t("component.analytics-widget.reach-adoption.company.action-btn")}
            </Button>
            <section className="text-left">
              <h4 className="text-heading-4">
                {t("component.analytics-widget.reach-adoption.company.action-title", {
                  count: missedCompanies,
                })}
              </h4>
              <p>
                {t("component.analytics-widget.reach-adoption.company.action-description", {
                  count: missedCompanies,
                })}
              </p>
            </section>
          </Stack>
        </AnalyticsWidgetCard.Footer>
      )}
    </AnalyticsWidgetCard.Root>
  );
}
